import { useEffect, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { AudienceHeatMapProps, HeatPoint } from '../types';

// Fix Leaflet default marker icons (same as ScreenSelection/Maps.tsx)
if ('_getIconUrl' in L.Icon.Default.prototype) {
  delete (L.Icon.Default.prototype as any)._getIconUrl;
}
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Red pin for uploaded sites - visually distinct from the blue city pins
// (same color-marker set used by ScreenSelection/Maps.tsx)
const siteIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const HEAT_GRADIENT: Record<number, string> = {
  0.25: '#2c7fb8',
  0.45: '#41b6c4',
  0.6: '#a1dab4',
  0.75: '#fecc5c',
  0.88: '#fd8d3c',
  1.0: '#e31a1c',
};

// Canvas heat rendering (self-contained - no external plugin)
/** 256-colour lookup table built from the gradient stops. */
const buildPalette = (gradient: Record<number, string>): Uint8ClampedArray => {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 1;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createLinearGradient(0, 0, 256, 0);
  Object.entries(gradient).forEach(([stop, color]) => {
    grad.addColorStop(Number(stop), color);
  });
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 1);
  return ctx.getImageData(0, 0, 256, 1).data;
};

/** Soft grayscale dot stamped once per data point. */
const createStamp = (radius: number, blur: number): HTMLCanvasElement => {
  const fullRadius = radius + blur;
  const stamp = document.createElement('canvas');
  stamp.width = stamp.height = fullRadius * 2;
  const ctx = stamp.getContext('2d')!;
  const grad = ctx.createRadialGradient(
    fullRadius,
    fullRadius,
    0,
    fullRadius,
    fullRadius,
    fullRadius
  );
  grad.addColorStop(0, 'rgba(0,0,0,1)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, fullRadius * 2, fullRadius * 2);
  return stamp;
};

const drawHeat = (
  map: L.Map,
  canvas: HTMLCanvasElement,
  points: HeatPoint[],
  radius: number,
  palette: Uint8ClampedArray
) => {
  const size = map.getSize();
  if (size.x === 0 || size.y === 0) return;

  canvas.width = size.x;
  canvas.height = size.y;
  const topLeft = map.containerPointToLayerPoint([0, 0]);
  L.DomUtil.setPosition(canvas, topLeft);

  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size.x, size.y);
  if (points.length === 0) return;

  const blur = Math.round(radius * 0.85);
  const stamp = createStamp(radius, blur);
  const fullRadius = radius + blur;

  // Accumulate grayscale intensity
  points.forEach(point => {
    const cp = map.latLngToContainerPoint([point.lat, point.lng]);
    if (
      cp.x < -fullRadius ||
      cp.y < -fullRadius ||
      cp.x > size.x + fullRadius ||
      cp.y > size.y + fullRadius
    ) {
      return;
    }
    ctx.globalAlpha = Math.min(Math.max(point.weight, 0.05), 1);
    ctx.drawImage(stamp, cp.x - fullRadius, cp.y - fullRadius);
  });
  ctx.globalAlpha = 1;

  // Colourize accumulated alpha through the palette.
  // Gamma boost (0.55) spreads the full blue→red range across realistic
  // intensities - without it, sparse points never leave the blue zone.
  const image = ctx.getImageData(0, 0, size.x, size.y);
  const pixels = image.data;
  for (let i = 0; i < pixels.length; i += 4) {
    const alpha = pixels[i + 3];
    // Trim the near-invisible skirt so the map isn't washed in faint blue
    if (alpha < 8) {
      pixels[i + 3] = 0;
      continue;
    }
    const boosted = Math.min(255, Math.round(Math.pow(alpha / 255, 0.7) * 255));
    const paletteIndex = boosted * 4;
    pixels[i] = palette[paletteIndex];
    pixels[i + 1] = palette[paletteIndex + 1];
    pixels[i + 2] = palette[paletteIndex + 2];
    // Lift visibility, but cap so the basemap stays readable under hot zones
    pixels[i + 3] = Math.min(210, Math.round(alpha * 1.15) + 25);
  }
  ctx.putImageData(image, 0, 0);
};

function HeatLayer({
  points,
  radius,
}: {
  points: HeatPoint[];
  radius: number;
}) {
  const map = useMap();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stateRef = useRef({ points, radius });
  const redrawRef = useRef<() => void>(() => {});
  const paletteRef = useRef<Uint8ClampedArray | null>(null);
  if (!paletteRef.current) {
    paletteRef.current = buildPalette(HEAT_GRADIENT);
  }

  useEffect(() => {
    // Dedicated pane between the tiles (200) and the marker pane (600) so
    // pins always stay visible on top of the heat wash.
    let pane = map.getPane('heat-pane');
    if (!pane) {
      pane = map.createPane('heat-pane');
      pane.style.zIndex = '350';
      pane.style.pointerEvents = 'none';
    }

    const canvas = document.createElement('canvas');
    canvas.className = 'leaflet-zoom-hide';
    canvas.style.position = 'absolute';
    canvas.style.pointerEvents = 'none';
    pane.appendChild(canvas);
    canvasRef.current = canvas;

    const redraw = () => {
      drawHeat(
        map,
        canvas,
        stateRef.current.points,
        stateRef.current.radius,
        paletteRef.current!
      );
    };
    redrawRef.current = redraw;
    map.on('moveend zoomend resize viewreset', redraw);
    redraw();

    return () => {
      map.off('moveend zoomend resize viewreset', redraw);
      canvas.remove();
      canvasRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    stateRef.current = { points, radius };
    redrawRef.current();
  }, [points, radius]);

  return null;
}

function FitBounds({ points }: { points: HeatPoint[] }) {
  const map = useMap();
  const lastSignatureRef = useRef('');

  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(
      points.map(point => [point.lat, point.lng] as [number, number])
    );
    // Only re-fit when the covered area actually changes - weight-only
    // updates (select/deselect filters) must not reset the user's view.
    const signature = bounds.toBBoxString();
    if (signature === lastSignatureRef.current) return;
    lastSignatureRef.current = signature;
    map.fitBounds(bounds.pad(0.08), { maxZoom: 10, animate: true });
  }, [points, map]);

  return null;
}

// Main component
const AudienceHeatMap = ({
  points,
  pins = [],
  radius = 26,
}: AudienceHeatMapProps) => {
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        position: 'relative',
        minHeight: '300px',
      }}
    >
      <MapContainer
        center={[21.5, 78.9]}
        zoom={5}
        style={{ height: '100%', width: '100%', minHeight: '300px' }}
      >
        {/* Muted light basemap so the heat wash reads clearly */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />

        <HeatLayer points={points} radius={radius} />
        <FitBounds points={points} />

        {/* City pins — standard markers */}
        {pins
          .filter(pin => pin.type === 'city')
          .map(pin => (
            <Marker key={`city-${pin.label}`} position={[pin.lat, pin.lng]}>
              <Tooltip direction="top" offset={[-15, -10]}>
                {pin.label}
              </Tooltip>
            </Marker>
          ))}

        {/* Site pins — red teardrop markers */}
        {pins
          .filter(pin => pin.type === 'site')
          .map((pin, index) => (
            <Marker
              key={`site-${index}-${pin.lat}-${pin.lng}`}
              position={[pin.lat, pin.lng]}
              icon={siteIcon}
            >
              <Tooltip direction="top" offset={[1, -34]}>
                {pin.label}
              </Tooltip>
            </Marker>
          ))}
      </MapContainer>

      {points.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            background: 'white',
            padding: '10px 14px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            fontSize: 12,
            color: '#666',
          }}
        >
          No audience density data yet
        </div>
      )}
    </div>
  );
};

export default AudienceHeatMap;
